import { motion } from "framer-motion";
import {
  Plug,
  FolderGit2,
  Inbox,
  Slack,
  Ticket,
  Github,
  Calendar,
  ShieldCheck,
  Network,
  Sparkles,
} from "lucide-react";
import { useHive } from "@/store/useHive";
import { isVisible } from "@/lib/entitlements";
import type { AppId } from "@/types";

const accent = "#06b6d4";

type Node = {
  id: string;
  label: string;
  icon: any;
  color: string;
  ring: "inner" | "outer";
  angle: number; // degrees
};

// inner ring = Hive subsystems · outer ring = external integrations
const NODES: Node[] = [
  { id: "workspaces", label: "Workspaces", icon: FolderGit2, color: "#14b8a6", ring: "inner", angle: -90 },
  { id: "tasks", label: "Tasks", icon: Inbox, color: "#f97316", ring: "inner", angle: -18 },
  { id: "ares", label: "ARES", icon: ShieldCheck, color: "#f59e0b", ring: "inner", angle: 54 },
  { id: "byonc", label: "BYONC", icon: Network, color: "#10b981", ring: "inner", angle: 126 },
  { id: "garlic", label: "Garlic", icon: Sparkles, color: "#a855f7", ring: "inner", angle: 198 },
  { id: "slack", label: "Slack", icon: Slack, color: "#e01e5a", ring: "outer", angle: -54 },
  { id: "jira", label: "Jira", icon: Ticket, color: "#2684ff", ring: "outer", angle: 18 },
  { id: "github", label: "GitHub", icon: Github, color: "#8b5cf6", ring: "outer", angle: 90 },
  { id: "calendar", label: "Calendar", icon: Calendar, color: "#4285f4", ring: "outer", angle: 162 },
  { id: "cloud", label: "Cloud", icon: Network, color: "#ff9900", ring: "outer", angle: 234 },
];

/** Hive-subsystem nodes that map to gated products. */
const PRODUCT_NODES = new Set(["ares", "byonc", "garlic"]);

function pos(n: Node) {
  const R = n.ring === "inner" ? 24 : 40;
  const rad = (n.angle * Math.PI) / 180;
  return { x: 50 + Math.cos(rad) * R, y: 50 + Math.sin(rad) * R };
}

export function Topology() {
  const entitlements = useHive((s) => s.entitlements);
  // Hidden products disappear from the map; other states keep their node
  // (the topology shows what the platform connects, not what's unlocked).
  const nodes = NODES.filter(
    (n) => !PRODUCT_NODES.has(n.id) || isVisible(entitlements, n.id as AppId),
  );
  return (
    <div className="relative mx-auto aspect-square w-full max-w-[560px]">
      <svg viewBox="0 0 100 100" className="absolute inset-0 h-full w-full">
        {/* faint orbit rings */}
        <circle cx={50} cy={50} r={24} fill="none" stroke="hsl(var(--border))" strokeWidth={0.3} opacity={0.5} />
        <circle cx={50} cy={50} r={40} fill="none" stroke="hsl(var(--border))" strokeWidth={0.3} opacity={0.4} />

        {nodes.map((n, i) => {
          const p = pos(n);
          return (
            <g key={n.id}>
              <line x1={50} y1={50} x2={p.x} y2={p.y} stroke={n.color} strokeWidth={0.35} opacity={0.4} />
              {/* flowing packet */}
              <motion.circle
                r={0.7}
                fill={n.color}
                initial={{ cx: 50, cy: 50 }}
                animate={{ cx: [50, p.x, 50], cy: [50, p.y, 50] }}
                transition={{ duration: 3 + (i % 4) * 0.6, repeat: Infinity, ease: "easeInOut", delay: i * 0.25 }}
              />
            </g>
          );
        })}
      </svg>

      {/* hub */}
      <motion.div
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ type: "spring", stiffness: 200, damping: 18 }}
        className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"
      >
        <div className="grid h-16 w-16 place-items-center rounded-2xl text-white shadow-glow" style={{ background: `linear-gradient(135deg, ${accent}, ${accent}aa)` }}>
          <Plug size={26} />
        </div>
        <div className="mt-1.5 text-center text-[11px] font-semibold text-app">Hive</div>
      </motion.div>

      {nodes.map((n, i) => {
        const p = pos(n);
        return (
          <motion.div
            key={n.id}
            className="absolute -translate-x-1/2 -translate-y-1/2"
            style={{ left: `${p.x}%`, top: `${p.y}%` }}
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: 0.15 + i * 0.06, type: "spring", stiffness: 300, damping: 20 }}
            whileHover={{ scale: 1.12 }}
          >
            <div className="grid place-items-center">
              <div
                className="grid h-11 w-11 place-items-center rounded-xl border bg-surface shadow-lg"
                style={{ borderColor: `${n.color}55`, color: n.color }}
              >
                <n.icon size={18} />
              </div>
              <div className="mt-1 whitespace-nowrap text-[10px] font-medium text-app-1">{n.label}</div>
            </div>
          </motion.div>
        );
      })}
    </div>
  );
}
